import React from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";

export type TestMultiSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type TestMultiSelectGroup = {
  label: string;
  /** Leaf options under this group (used when there are no subgroups). */
  options?: TestMultiSelectOption[];
  /** Nested groups (e.g. subject → topic → subtopic options). */
  subgroups?: TestMultiSelectGroup[];
};

type TestMultiSelectProps = {
  label: string;
  value: string[];
  onChange: (values: string[]) => void;
  options?: TestMultiSelectOption[];
  /** When set, options are rendered under group headers (e.g. by subject). */
  groups?: TestMultiSelectGroup[];
  disabled?: boolean;
  /** Dropdown can open to view selections, but values cannot be changed. */
  readOnly?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  labelId?: string;
  className?: string;
};

const flattenGroupOptions = (
  groups: TestMultiSelectGroup[]
): TestMultiSelectOption[] =>
  groups.flatMap((g) => [
    ...(g.options || []),
    ...(g.subgroups ? flattenGroupOptions(g.subgroups) : []),
  ]);

const groupHasItems = (group: TestMultiSelectGroup): boolean =>
  (group.options?.length || 0) > 0 ||
  Boolean(group.subgroups?.some(groupHasItems));

const TestMultiSelect: React.FC<TestMultiSelectProps> = ({
  label,
  value,
  onChange,
  options = [],
  groups,
  disabled = false,
  readOnly = false,
  placeholder = "Select...",
  fullWidth = true,
  labelId,
  className,
}) => {
  const id =
    labelId || `test-multi-${label.toLowerCase().replace(/\s+/g, "-")}`;

  const flatOptions = groups?.length
    ? flattenGroupOptions(groups)
    : options;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    if (readOnly) return;
    const next = event.target.value;
    // Ignore accidental selection of group header keys
    const values = (typeof next === "string" ? next.split(",") : next).filter(
      (v) => !String(v).startsWith("__group__:")
    );
    onChange(values);
  };

  const renderValue = (selected: string[]) => {
    if (!selected.length) return placeholder;
    const labels = flatOptions
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    // Fewer than 3: show names; otherwise "N selected"
    if (labels.length < 3) return labels.join(", ");
    return `${labels.length} selected`;
  };

  const hasItems = groups?.length
    ? groups.some(groupHasItems)
    : options.length > 0;

  const optionDisabled = (optionDisabledFlag?: boolean) =>
    Boolean(optionDisabledFlag || readOnly);

  const renderOption = (option: TestMultiSelectOption) => (
    <MenuItem
      key={option.value}
      value={option.value}
      disabled={optionDisabled(option.disabled)}
    >
      <Checkbox
        size="small"
        checked={value.includes(option.value)}
        disabled={optionDisabled(option.disabled)}
      />
      <ListItemText primary={option.label} />
    </MenuItem>
  );

  const renderGroupNodes = (
    group: TestMultiSelectGroup,
    path: string,
    depth: number
  ): React.ReactNode[] => {
    const headerKey = `__group__:${path}`;
    const isSubjectLevel = depth === 0 && Boolean(group.subgroups?.length);
    const nodes: React.ReactNode[] = [
      <MenuItem
        key={headerKey}
        value={headerKey}
        disabled
        dense
        sx={{
          opacity: "1 !important",
          fontWeight: isSubjectLevel ? 700 : 600,
          fontSize: isSubjectLevel ? "0.8rem" : "0.75rem",
          bgcolor: isSubjectLevel ? "action.hover" : "action.selected",
          pl: 1.5 + depth * 1.5,
          py: 0.75,
        }}
      >
        {group.label}
      </MenuItem>,
    ];

    if (group.subgroups?.length) {
      for (const sub of group.subgroups) {
        if (!groupHasItems(sub)) continue;
        nodes.push(
          ...renderGroupNodes(sub, `${path}/${sub.label}`, depth + 1)
        );
      }
    }

    for (const option of group.options || []) {
      nodes.push(renderOption(option));
    }

    return nodes;
  };

  return (
    <FormControl
      variant="outlined"
      size="small"
      className={className}
      fullWidth={fullWidth}
      sx={fullWidth ? { width: "100%", minWidth: 0 } : undefined}
    >
      <InputLabel id={id} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={id}
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} notched />}
        renderValue={renderValue}
        disabled={disabled}
        displayEmpty
      >
        {!hasItems ? (
          <MenuItem disabled>No options</MenuItem>
        ) : groups?.length ? (
          groups.flatMap((group) =>
            groupHasItems(group)
              ? renderGroupNodes(group, group.label, 0)
              : []
          )
        ) : (
          options.map((option) => renderOption(option))
        )}
      </Select>
    </FormControl>
  );
};

export default TestMultiSelect;
